import { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  IMPERSONATABLE_ROLES,
  Role,
  type AdminItMemberDto,
  type ImpersonationTargetDto,
} from '@ciimar/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { useT } from '../lib/i18n';

const ROLE_LABEL: Partial<Record<Role, string>> = {
  [Role.Researcher]: 'settings.it.role.researcher',
  [Role.Onboarding]: 'settings.it.role.onboarding',
  [Role.Hr]: 'settings.it.role.hr',
  [Role.Direction]: 'settings.it.role.direction',
};

/**
 * IT administration (Definições → Administração de TI): impersonate a user to
 * reproduce or help with what they see, and manage the IT-admin list. Visible
 * only to IT admins — impersonation grants effective global access.
 */
export function SettingsIt() {
  const t = useT();
  const { isAdminIt, loading } = useAuth();

  if (loading) return <p className="muted">{t('common.loading')}</p>;
  if (!isAdminIt) return <Navigate to="/" replace />;

  return (
    <div className="it-page">
      <p className="muted">{t('settings.it.intro')}</p>
      <ImpersonatePanel />
      <AdminItListPanel />
    </div>
  );
}

function ImpersonatePanel() {
  const t = useT();
  const [q, setQ] = useState('');
  const [targets, setTargets] = useState<ImpersonationTargetDto[]>([]);
  const [selected, setSelected] = useState<ImpersonationTargetDto | null>(null);
  const [roles, setRoles] = useState<Role[]>([Role.Researcher]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Debounced target search.
  useEffect(() => {
    let active = true;
    const handle = setTimeout(() => {
      api
        .impersonationTargets(q || undefined)
        .then((rows) => active && setTargets(rows))
        .catch(() => active && setTargets([]));
    }, 250);
    return () => {
      active = false;
      clearTimeout(handle);
    };
  }, [q]);

  function toggleRole(r: Role) {
    setRoles((cur) => (cur.includes(r) ? cur.filter((x) => x !== r) : [...cur, r]));
  }

  async function start() {
    if (!selected || roles.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      await api.startImpersonation({
        username: selected.username,
        displayName: selected.name,
        roles,
      });
      // Full reload so every context refetches as the impersonated user.
      window.location.href = '/';
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  }

  return (
    <section className="erp-panel it-panel">
      <div className="erp-toolbar">
        <strong>{t('settings.it.impersonate.title')}</strong>
      </div>
      <p className="muted it-panel-hint">{t('settings.it.impersonate.hint')}</p>

      <input
        className="erp-input"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={t('settings.it.impersonate.searchPlaceholder')}
      />

      <div className="it-targets">
        {targets.length === 0 ? (
          <div className="panel-empty is-empty">{t('settings.it.impersonate.noResults')}</div>
        ) : (
          targets.map((p) => (
            <button
              type="button"
              key={p.personId}
              className={`it-target${selected?.personId === p.personId ? ' on' : ''}`}
              onClick={() => setSelected(p)}
            >
              <span className="it-target-name">{p.name}</span>
              <span className="it-target-email">{p.email}</span>
            </button>
          ))
        )}
      </div>

      {selected && (
        <div className="it-start">
          <div className="it-roles">
            <span className="it-roles-label">{t('settings.it.impersonate.rolesLabel')}</span>
            <div className="it-roles-chips">
              {IMPERSONATABLE_ROLES.map((r) => (
                <button
                  type="button"
                  key={r}
                  className={`fcard-flag${roles.includes(r) ? ' on' : ''}`}
                  onClick={() => toggleRole(r)}
                >
                  {roles.includes(r) ? '✓ ' : ''}
                  {t(ROLE_LABEL[r] ?? '')}
                </button>
              ))}
            </div>
          </div>
          {error && <div className="alert error">{error}</div>}
          <button type="button" onClick={() => void start()} disabled={busy || roles.length === 0}>
            {busy
              ? t('settings.it.impersonate.starting')
              : t('settings.it.impersonate.start', { name: selected.name })}
          </button>
        </div>
      )}
    </section>
  );
}

function AdminItListPanel() {
  const t = useT();
  const [members, setMembers] = useState<AdminItMemberDto[]>([]);
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    api
      .adminItMembers()
      .then(setMembers)
      .catch((err) => setError((err as Error).message));
  }, []);
  useEffect(() => load(), [load]);

  async function add(e: React.FormEvent) {
    e.preventDefault();
    if (!username.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await api.addAdminItMember({
        username: username.trim(),
        displayName: displayName.trim() || null,
      });
      setUsername('');
      setDisplayName('');
      load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function remove(u: string) {
    if (!window.confirm(t('settings.it.list.removeConfirm', { username: u }))) return;
    setError(null);
    try {
      await api.removeAdminItMember(u);
      load();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <section className="erp-panel it-panel">
      <div className="erp-toolbar">
        <strong>{t('settings.it.list.title')}</strong>
        <span className="page-sub">{members.length}</span>
      </div>
      <p className="muted it-panel-hint">{t('settings.it.list.hint')}</p>

      {error && <div className="alert error">{error}</div>}

      <div className="it-members">
        {members.map((m) => (
          <div className="it-member" key={m.username}>
            <div className="it-member-id">
              <span className="it-member-name">{m.displayName || m.username}</span>
              <span className="it-member-user">{m.username}</span>
            </div>
            <button
              type="button"
              className="link-button danger"
              onClick={() => void remove(m.username)}
            >
              {t('common.remove')}
            </button>
          </div>
        ))}
      </div>

      <form className="it-add" onSubmit={add}>
        <input
          className="erp-input"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder={t('settings.it.list.usernamePlaceholder')}
        />
        <input
          className="erp-input"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder={t('settings.it.list.namePlaceholder')}
        />
        <button type="submit" disabled={busy || !username.trim()}>
          {t('settings.it.list.add')}
        </button>
      </form>
    </section>
  );
}
